import {
  type ColumnOrderState,
  type OnChangeFn,
  type Updater,
  type ColumnVisibilityState,
} from '@tanstack/react-table';
import {
  type PropsWithChildren,
  type ReactNode,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';

import type { DataTableColumnDef } from '@/components/DataTable';
import {
  getVisibleTableColumnsState,
  setVisibleTableColumnsState,
} from '@/helper/user-preferences';
import type { FilterComponentProps } from '@/providers/TableState/types';

type TableKey =
  | 'paidSearchAdGroupActivity'
  | 'paidSearchKeywordActivity'
  | 'campaignActivity'
  | 'companyActivity'
  | 'offlineActivity'
  | 'vendorActivity'
  | 'vendorCampaignActivity';

type ColumnPreferencesState = {
  sortedColumns: string[];
  hiddenColumns: string[];
};

export type TableStateColumn = {
  id: string;
  title?: string;
  hidden?: boolean;
  metricSelector?: boolean | { title: string };
  advancedFilter?: boolean | { title: string };
  filterComponent?: (props: FilterComponentProps) => ReactNode;
  infoPopover?: ReactNode;
};

type TableStateContextType = {
  columns: TableStateColumn[];
  // Declaration-order columns, unaffected by the user's `sortedColumns` preference.
  // Use this when a UI surface (e.g. the advanced filters dialog) should stay in a
  // stable order regardless of how the table itself is reordered.
  originalColumns: TableStateColumn[];
  columnsMap: Record<string, TableStateColumn>;
  columnsState: ColumnPreferencesState;
  columnOrder: string[];
  columnVisibility: ColumnVisibilityState;
  onColumnsStateChange: (nextState: ColumnPreferencesState) => void;
  onColumnOrderChange: OnChangeFn<ColumnOrderState>;
  onColumnVisibilityChange: OnChangeFn<ColumnVisibilityState>;
};

const TableStateContext = createContext<TableStateContextType | undefined>(undefined);

export const useTableState = () => {
  const context = useContext(TableStateContext);
  if (!context) {
    throw new Error('useTableState must be used within a TableState');
  }
  return context;
};

export const useOptionalTableState = () => useContext(TableStateContext);

type TableStateDataTableColumnDef<TData extends object> = DataTableColumnDef<TData> & {
  metricSelector?: boolean | { title: string };
  advancedFilter?: boolean | { title: string };
  infoPopover?: ReactNode;
  columns?: TableStateDataTableColumnDef<TData>[];
};

type Props<TData extends object> = {
  tableKey: TableKey;
  columns: TableStateDataTableColumnDef<TData>[];
  defaultHiddenColumns?: string[];
};

const getColumnId = <TData extends object>(
  column: TableStateDataTableColumnDef<TData>,
): string | undefined => {
  if (column.id != null) {
    return column.id;
  }

  if ('accessorKey' in column && column.accessorKey != null) {
    return String(column.accessorKey);
  }

  return undefined;
};

const resolveUpdater = <TValue,>(updater: Updater<TValue>, previous: TValue): TValue => {
  return typeof updater === 'function' ? (updater as (old: TValue) => TValue)(previous) : updater;
};

const normalizeSortedColumns = (sortedColumns: string[], allColumnIds: string[]) => {
  const uniqueInBounds = sortedColumns.filter(
    (columnId, index) =>
      allColumnIds.includes(columnId) && sortedColumns.indexOf(columnId) === index,
  );

  const normalized = [...uniqueInBounds];
  const missingColumnIds = allColumnIds.filter((columnId) => !uniqueInBounds.includes(columnId));

  missingColumnIds.forEach((missingColumnId) => {
    const declarationIndex = allColumnIds.indexOf(missingColumnId);
    const precedingColumnIds = allColumnIds.slice(0, declarationIndex);

    let insertAfterIndex = -1;
    for (let index = precedingColumnIds.length - 1; index >= 0; index -= 1) {
      const precedingColumnId = precedingColumnIds[index];
      const normalizedIndex = normalized.indexOf(precedingColumnId);
      if (normalizedIndex !== -1) {
        insertAfterIndex = normalizedIndex;
        break;
      }
    }

    normalized.splice(insertAfterIndex + 1, 0, missingColumnId);
  });

  return normalized;
};

const normalizeHiddenColumns = (
  hiddenColumns: string[],
  allColumnIds: string[],
  staticHiddenColumns: Set<string>,
) =>
  hiddenColumns.filter(
    (columnId, index) =>
      allColumnIds.includes(columnId) &&
      !staticHiddenColumns.has(columnId) &&
      hiddenColumns.indexOf(columnId) === index,
  );

const TableState = <TData extends object>({
  tableKey,
  columns,
  defaultHiddenColumns = [],
  children,
}: PropsWithChildren<Props<TData>>) => {
  const leafColumns = useMemo(() => {
    const collected: TableStateColumn[] = [];

    const visit = (column: TableStateDataTableColumnDef<TData>) => {
      if ('columns' in column && Array.isArray(column.columns)) {
        column.columns.forEach((childColumn) => visit(childColumn));
        return;
      }

      const columnId = getColumnId(column);
      if (!columnId) {
        return;
      }

      collected.push({
        id: columnId,
        title: typeof column.header === 'string' ? column.header : undefined,
        hidden: column.hidden === true,
        metricSelector: column.metricSelector,
        advancedFilter: column.advancedFilter,
        filterComponent: column.filterComponent,
        infoPopover: column.infoPopover,
      });
    };

    columns.forEach((column) => visit(column));
    return collected;
  }, [columns]);

  const columnIds = useMemo(() => leafColumns.map((column) => column.id), [leafColumns]);
  const staticHiddenColumns = useMemo(
    () =>
      new Set(leafColumns.filter((column) => column.hidden === true).map((column) => column.id)),
    [leafColumns],
  );

  const [columnsState, setColumnsState] = useState<ColumnPreferencesState>(() => {
    const userPreferences = getVisibleTableColumnsState(tableKey);

    if (userPreferences) {
      return {
        sortedColumns: normalizeSortedColumns(userPreferences.sortedColumns, columnIds),
        hiddenColumns: normalizeHiddenColumns(
          userPreferences.hiddenColumns,
          columnIds,
          staticHiddenColumns,
        ),
      };
    }

    return {
      sortedColumns: normalizeSortedColumns(columnIds, columnIds),
      hiddenColumns: normalizeHiddenColumns(defaultHiddenColumns, columnIds, staticHiddenColumns),
    };
  });

  const columnsMap = useMemo(
    () =>
      leafColumns.reduce<Record<string, TableStateColumn>>((acc, column) => {
        acc[column.id] = column;
        return acc;
      }, {}),
    [leafColumns],
  );

  const sanitizeColumnsState = (nextState: ColumnPreferencesState): ColumnPreferencesState => ({
    sortedColumns: normalizeSortedColumns(nextState.sortedColumns, columnIds),
    hiddenColumns: normalizeHiddenColumns(nextState.hiddenColumns, columnIds, staticHiddenColumns),
  });

  const onColumnsStateChange = (nextState: ColumnPreferencesState) => {
    const sanitizedState = sanitizeColumnsState(nextState);
    setColumnsState(sanitizedState);
    setVisibleTableColumnsState(tableKey, sanitizedState);
  };

  const onColumnOrderChange: OnChangeFn<ColumnOrderState> = (updater) => {
    setColumnsState((previousState) => {
      const nextColumnOrder = normalizeSortedColumns(
        resolveUpdater(updater, previousState.sortedColumns),
        columnIds,
      );

      const nextState = {
        sortedColumns: nextColumnOrder,
        hiddenColumns: previousState.hiddenColumns,
      };

      const sanitizedState = sanitizeColumnsState(nextState);
      setVisibleTableColumnsState(tableKey, sanitizedState);
      return sanitizedState;
    });
  };

  const onColumnVisibilityChange: OnChangeFn<ColumnVisibilityState> = (updater) => {
    setColumnsState((previousState) => {
      const previousVisibility: ColumnVisibilityState = previousState.hiddenColumns.reduce(
        (acc, columnId) => {
          acc[columnId] = false;
          return acc;
        },
        {} as ColumnVisibilityState,
      );

      const nextVisibility = resolveUpdater(updater, previousVisibility);
      const hiddenColumns = normalizeHiddenColumns(
        columnIds.filter(
          (columnId) => !staticHiddenColumns.has(columnId) && nextVisibility[columnId] === false,
        ),
        columnIds,
        staticHiddenColumns,
      );

      const nextState = {
        sortedColumns: previousState.sortedColumns,
        hiddenColumns,
      };

      setVisibleTableColumnsState(tableKey, nextState);
      return nextState;
    });
  };

  const orderedColumns = useMemo(() => {
    const orderedIds = normalizeSortedColumns(columnsState.sortedColumns, columnIds);
    return orderedIds.flatMap((columnId) => {
      const column = columnsMap[columnId];
      return column ? [column] : [];
    });
  }, [columnIds, columnsMap, columnsState.sortedColumns]);

  const sanitizedColumnsState = sanitizeColumnsState(columnsState);

  const columnVisibility = useMemo(() => {
    const visibility: ColumnVisibilityState = {};

    leafColumns.forEach((column) => {
      if (column.hidden === true || columnsState.hiddenColumns.includes(column.id)) {
        visibility[column.id] = false;
      }
    });

    return visibility;
  }, [leafColumns, columnsState.hiddenColumns]);

  const contextValue: TableStateContextType = {
    columns: orderedColumns,
    originalColumns: leafColumns,
    columnsMap,
    columnsState: sanitizedColumnsState,
    columnOrder: normalizeSortedColumns(sanitizedColumnsState.sortedColumns, columnIds),
    columnVisibility,
    onColumnsStateChange,
    onColumnOrderChange,
    onColumnVisibilityChange,
  };

  return <TableStateContext.Provider value={contextValue}>{children}</TableStateContext.Provider>;
};

export default TableState;
